import React, { useEffect, useMemo, useState } from "react";
import { Draggable } from "@hello-pangea/dnd";
import OrderDetails from "./OrderDetails";
import Modal from "./Modal";
import PinModal from "./PinModal";
import { adminApiRequest } from "../utils/adminApi";

const TWO_HOURS_MS = 2 * 60 * 60 * 1000;

const parseUkDateTime = (dateStr) => {
  if (!dateStr) return null;

  const s = String(dateStr).trim();
  const [datePart, timePart = "00:00:00"] = s.split(" ");
  if (!datePart) return null;

  const [y, m, d] = datePart.split("-").map(Number);
  const [hh, mm, ss] = timePart.split(":").map(Number);

  if (![y, m, d, hh, mm, ss].every((n) => Number.isFinite(n))) return null;

  const utcMs = Date.UTC(y, m - 1, d, hh, mm, ss);

  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/London",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(new Date(utcMs));

  const map = Object.fromEntries(parts.map((p) => [p.type, p.value]));

  const londonMs = Date.UTC(
    Number(map.year),
    Number(map.month) - 1,
    Number(map.day),
    Number(map.hour),
    Number(map.minute),
    Number(map.second),
  );

  return new Date(londonMs);
};

const getUkOpeningTime = (baseDate) => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/London",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(baseDate);

  const map = Object.fromEntries(parts.map((p) => [p.type, p.value]));

  return new Date(
    Date.UTC(
      Number(map.year),
      Number(map.month) - 1,
      Number(map.day),
      12,
      0,
      0,
    ),
  );
};

export default function OrderCard({
  order,
  index,
  disableDrag,
  expanded,
  onToggle,
  refetchOrders,
}) {
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);

  const [pinAction, setPinAction] = useState(null);
  const [completedRemovalUnlocked, setCompletedRemovalUnlocked] =
    useState(false);

  const status = String(order?.status || "")
    .trim()
    .toLowerCase();
  const isCompleted = status === "completed";

  const correctPin = process.env.REACT_APP_ADMIN_PIN || "";

  const receivedAt = useMemo(
    () => parseUkDateTime(order.created_at),
    [order.created_at],
  );

  const completedAt = useMemo(() => {
    if (!isCompleted) return null;
    if (order?.completed_at) return parseUkDateTime(order.completed_at);
    if (order?.moved_at) return parseUkDateTime(order.moved_at);
    return null;
  }, [isCompleted, order?.completed_at, order?.moved_at]);

  useEffect(() => {
    const safeReceivedAt =
      receivedAt ||
      (order?.created_at
        ? new Date(String(order.created_at).replace(" ", "T"))
        : null);

    if (!safeReceivedAt || Number.isNaN(safeReceivedAt.getTime())) return;

    let intervalId = null;

    const tick = () => {
      const end = completedAt ? completedAt : new Date();
      const openingTime = getUkOpeningTime(safeReceivedAt);

      const timerStart =
        safeReceivedAt.getTime() < openingTime.getTime()
          ? openingTime
          : safeReceivedAt;

      const diff = Math.max(0, end.getTime() - timerStart.getTime());
      const capped = Math.min(diff, TWO_HOURS_MS);

      setElapsedMs(capped);

      if (!completedAt && capped >= TWO_HOURS_MS && intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    tick();

    const now = new Date();
    const openingTime = getUkOpeningTime(safeReceivedAt);

    if (now >= openingTime && !completedAt) {
      intervalId = setInterval(tick, 1000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [receivedAt, completedAt, order?.created_at]);

  useEffect(() => {
    setCompletedRemovalUnlocked(false);
    setPinAction(null);
    setShowPinModal(false);
  }, [order?.id]);

  const formatElapsed = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, "0");
    const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(
      2,
      "0",
    );
    const seconds = String(totalSeconds % 60).padStart(2, "0");
    return `${hours}:${minutes}:${seconds}`;
  };

  const timerText = formatElapsed(elapsedMs);

  const orderDateText = useMemo(() => {
    const d = parseUkDateTime(order.created_at);
    if (!d) return "";
    return d.toLocaleDateString("en-GB", { timeZone: "Europe/London" });
  }, [order.created_at]);

  const formatWhatsAppPhone = (phone) => {
    if (!phone) return "";
    let cleaned = String(phone).replace(/\D/g, "");
    if (cleaned.startsWith("0")) cleaned = "234" + cleaned.slice(1);
    return cleaned;
  };

  const customerPhone = order?.customer_phone || "";
  const whatsappPhone = formatWhatsAppPhone(customerPhone);
  const canWhatsApp = Boolean(whatsappPhone);
  const isGuest = order?.customer_id == null;
  const showWhatsAppButton = !isGuest && canWhatsApp;

  const openWhatsApp = (e) => {
    e.stopPropagation();
    if (!showWhatsAppButton || loading) return;

    const customerName = order?.customer_name || "Customer";
    const orderRef = `ID ${order.id}`;
    const message = `Hi ${customerName}, regarding your order ${orderRef}.`;

    window.open(
      `https://wa.me/${whatsappPhone}?text=${encodeURIComponent(message)}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  const openCancelFlow = (e) => {
    e.stopPropagation();
    if (loading) return;

    if (isCompleted) {
      setPinAction("cancel");
      setShowPinModal(true);
      return;
    }

    setShowCancelModal(true);
  };

  const requestCompletedRemovalAccess = () => {
    if (!isCompleted) return true;
    if (completedRemovalUnlocked) return true;

    setPinAction("remove-item");
    setShowPinModal(true);
    return false;
  };

  const handlePinSuccess = () => {
    if (pinAction === "cancel") {
      setShowCancelModal(true);
    }

    if (pinAction === "remove-item") {
      setCompletedRemovalUnlocked(true);
    }

    setShowPinModal(false);
    setPinAction(null);
  };

  const closeCancelModal = () => {
    if (loading) return;
    setShowCancelModal(false);
  };

  const closePinModal = () => {
    if (loading) return;
    setShowPinModal(false);
    setPinAction(null);
  };

  const handleNotifyAndCancel = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (loading) return;

    const customerName = order.customer_name || "there";
    const orderRef = `ID ${order.id}`;

    const message = `Hi ${customerName}, your order ${orderRef} has been cancelled and will not be processed. Sorry for any inconvenience.`;

    if (canWhatsApp) {
      window.open(
        `https://wa.me/${whatsappPhone}?text=${encodeURIComponent(message)}`,
        "_blank",
        "noopener,noreferrer",
      );
    }

    setLoading(true);

    try {
      await adminApiRequest({
        script: "cancel-order",
        data: { order_id: order.id },
      });

      if (refetchOrders) await refetchOrders();

      setShowCancelModal(false);
    } catch (err) {
      alert(err.message || "Failed to cancel order.");
    } finally {
      setLoading(false);
    }
  };

  const statusColor = {
    "to do": "bg-red-500",
    "in progress": "bg-orange-400",
    completed: "bg-green-500",
  };

  const timerColorClass =
    status === "completed"
      ? "text-green-600"
      : status === "in progress"
        ? "text-orange-500"
        : "text-red-500";

  const cancelButtonText = loading
    ? "Cancelling..."
    : canWhatsApp
      ? "Notify and Cancel"
      : "Cancel Order";

  return (
    <>
      <Draggable
        draggableId={order.id.toString()}
        index={index}
        isDragDisabled={disableDrag}
      >
        {(provided) => (
          <li
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...(!disableDrag ? provided.dragHandleProps : {})}
            onClick={(e) => {
              const interactive = e.target.closest(
                "button, a, input, textarea, select, form",
              );
              if (interactive) return;
              onToggle();
            }}
            className={`relative list-none overflow-hidden rounded-2xl border bg-white px-4 py-3 shadow-sm transition-all duration-200 ${
              expanded
                ? "border-slate-300 shadow-md"
                : "border-slate-200 hover:border-slate-300 hover:shadow-md"
            }`}
            style={{
              ...provided.draggableProps.style,
              cursor: disableDrag ? "default" : "grab",
            }}
          >
            <div
              className={`absolute left-0 top-0 h-full w-1 ${
                statusColor[status] || "bg-slate-300"
              }`}
            />

            <div className="flex items-center gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-5 text-sm font-semibold">
                  <span className="text-black">{orderDateText}</span>
                  <span className="text-black">ID: {order.id}</span>
                  <span className={`ml-auto font-bold ${timerColorClass}`}>
                    {timerText}
                  </span>
                </div>

                <div className="mt-2 flex items-center gap-6 text-sm">
                  <span className="font-extrabold text-black">
                    Order #{order.order_number}
                  </span>

                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-purple-600">
                      {order.order_type}
                    </span>

                    {order.payment_method === "cash" && (
                      <span className="rounded bg-red-800 px-2 py-[2px] text-[10px] font-bold text-white">
                        PAY IN STORE
                      </span>
                    )}

                    {order.payment_method === "online" && (
                      <span className="rounded bg-green-800 px-2 py-[2px] text-[10px] font-bold text-white">
                        PAID ONLINE
                      </span>
                    )}
                  </div>

                  {order.order_type === "Eat In" && order.table_number && (
                    <span className="font-semibold text-black">
                      Table: {order.table_number}
                    </span>
                  )}

                  {isGuest && (
                    <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-bold tracking-[0.1em] text-slate-700">
                      GUEST
                    </span>
                  )}
                </div>

                <div className="mt-2 flex items-center">
                  <span className="text-sm font-bold text-black">Name:</span>

                  <span className="ml-2 text-sm text-black">
                    {order.customer_name || "Unnamed Customer"}
                  </span>

                  <span className="ml-auto text-xl font-extrabold text-[#fa9324]">
                    £{parseFloat(order.total_price || 0).toFixed(2)}
                  </span>
                </div>
              </div>

              <div className="flex shrink-0 flex-col items-center justify-center gap-2">
                {showWhatsAppButton && (
                  <button
                    type="button"
                    onClick={openWhatsApp}
                    disabled={loading}
                    className={`transition ${
                      loading
                        ? "cursor-not-allowed text-slate-300"
                        : "text-green-500 hover:text-green-600"
                    }`}
                    title="Message customer on WhatsApp"
                  >
                    <i className="fa-brands fa-whatsapp text-[22px]" />
                  </button>
                )}

                <button
                  type="button"
                  onClick={openCancelFlow}
                  disabled={loading}
                  className={`transition ${
                    loading
                      ? "cursor-not-allowed text-slate-300"
                      : "text-red-500 hover:text-red-600"
                  }`}
                  title="Reject order"
                >
                  <i className="fa-regular fa-circle-xmark text-[22px]" />
                </button>
              </div>
            </div>

            {expanded && (
              <div className="mt-4 border-t border-slate-200 pt-4">
                <div className="rounded-2xl bg-slate-50 p-4 ring-1 ring-slate-200">
                  <OrderDetails
                    order={order}
                    refetchOrders={refetchOrders}
                    completedRemovalUnlocked={completedRemovalUnlocked}
                    requestCompletedRemovalAccess={
                      requestCompletedRemovalAccess
                    }
                  />
                </div>
              </div>
            )}
          </li>
        )}
      </Draggable>

      <PinModal
        isOpen={showPinModal}
        onClose={closePinModal}
        onSuccess={handlePinSuccess}
        correctPin={correctPin}
      />

      {showCancelModal && (
        <Modal
          title={loading ? "Cancelling Order..." : "Cancel Order"}
          onClose={closeCancelModal}
          onSubmit={handleNotifyAndCancel}
          submitButtonText={cancelButtonText}
          showCancel={!loading}
          showSubmitButton={true}
          submitButtonClass={`text-white ${
            loading
              ? "cursor-not-allowed bg-red-400"
              : "bg-red-600 hover:bg-red-700"
          }`}
        >
          <div className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-4">
            <p className="text-sm font-medium text-rose-700">
              {loading
                ? "Please wait while the order is being cancelled."
                : "Are you sure you want to cancel this order?"}
            </p>

            {!loading && canWhatsApp && (
              <p className="mt-2 text-xs text-rose-600/80">
                The customer will be notified on WhatsApp before the order is
                cancelled.
              </p>
            )}

            {loading && (
              <div className="mt-3 flex items-center gap-2 text-sm font-medium text-rose-700">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-rose-300 border-t-rose-700" />
                <span>Processing cancellation...</span>
              </div>
            )}
          </div>
        </Modal>
      )}
    </>
  );
}
